<script setup lang="ts">
import { ref, onMounted, inject, Ref, computed } from 'vue'
import { useFeeds } from '@/services/Feeds'
import { Feed } from '@/types/Feeds'
import { Member } from '@/types/Member'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { EllipsisVerticalIcon, PlusCircleIcon } from '@heroicons/vue/24/solid'
import { Rss, RefreshCw, AlertCircle, CheckCircle2, PauseCircle } from 'lucide-vue-next'
import { toast } from 'vue-sonner'

const { feeds, feedItems, loading, error, getFeeds, getFeedItems, createFeed, deleteFeed, refreshFeed } = useFeeds()
const vhostId = inject<Ref<string> | null>('vhostId', null)
const userInfo = inject<Ref<Member> | null>('userInfo', null)

const showCreateDialog = ref(false)
const searchQuery = ref('')
const newFeed = ref({
	name: '',
	url: '',
	type: 'rss' as 'rss' | 'atom' | 'json',
	autoImport: false,
})

const filteredFeeds = computed(() => {
	if (!searchQuery.value) {
		return feeds.value
	}

	const query = searchQuery.value.toLowerCase()

	return feeds.value.filter((f) =>
		f.name.toLowerCase().includes(query) ||
		f.url.toLowerCase().includes(query)
	)
})

const getStatusVariant = (status: string) => {
	if (status === 'active') {
		return 'default'
	}

	if (status === 'error') {
		return 'destructive'
	}

	return 'secondary'
}

const getStatusIcon = (status: string) => {
	if (status === 'active') {
		return CheckCircle2
	}

	if (status === 'error') {
		return AlertCircle
	}

	return PauseCircle
}

const handleCreate = async () => {
	if (!vhostId || !vhostId.value || !newFeed.value.name || !newFeed.value.url) {
		return
	}

	const result = await createFeed(vhostId.value, newFeed.value)
	if (result) {
		toast('Feed Created', { description: `"${newFeed.value.name}" has been added.` })
		newFeed.value = { name: '', url: '', type: 'rss' as 'rss' | 'atom' | 'json', autoImport: false }
		showCreateDialog.value = false
		await getFeeds(vhostId.value)
	} else {
		toast('Error', { description: 'Failed to create feed.' })
	}
}

const handleRefresh = async (feed: Feed) => {
	const success = await refreshFeed(feed.id)
	if (success) {
		toast('Feed Refreshed', { description: `"${feed.name}" is being updated.` })
	} else {
		toast('Error', { description: 'Failed to refresh feed.' })
	}
}

const handleDelete = async (feed: Feed) => {
	const success = await deleteFeed(feed.id)
	if (success && vhostId && vhostId.value) {
		toast('Feed Deleted', { description: `"${feed.name}" has been removed.` })
		await getFeeds(vhostId.value)
	}
}

onMounted(async () => {
	if (vhostId && vhostId.value) {
		await getFeeds(vhostId.value)
	}
})
</script>

<template>
	<div class="p-4 space-y-4">
		<div class="grid gap-4 md:grid-cols-3">
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Total Feeds</CardTitle>
					<Rss class="h-5 w-5 text-muted-foreground" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ feeds.length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Active</CardTitle>
					<CheckCircle2 class="h-5 w-5 text-green-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ feeds.filter((f) => f.status === 'active').length }}</div>
				</CardContent>
			</Card>
			<Card>
				<CardHeader class="flex flex-row items-center justify-between space-y-0 pb-2">
					<CardTitle class="text-sm font-medium">Errors</CardTitle>
					<AlertCircle class="h-5 w-5 text-red-500" />
				</CardHeader>
				<CardContent>
					<div class="text-2xl font-bold">{{ feeds.filter((f) => f.status === 'error').length }}</div>
				</CardContent>
			</Card>
		</div>

		<Card>
			<CardHeader>
				<div class="flex justify-between items-center">
					<div>
						<CardTitle class="text-lg font-normal">Feed Manager</CardTitle>
						<CardDescription>Import content from RSS, Atom, and JSON feeds</CardDescription>
					</div>
					<Dialog v-model:open="showCreateDialog">
						<DialogTrigger as-child>
							<Button>
								<PlusCircleIcon class="h-4 w-4 mr-2" />
								Add Feed
							</Button>
						</DialogTrigger>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Add Feed</DialogTitle>
								<DialogDescription>Add a new content feed to import media from</DialogDescription>
							</DialogHeader>
							<div class="space-y-4 py-4">
								<div class="space-y-2">
									<Label for="feedName">Name</Label>
									<Input id="feedName" v-model="newFeed.name" placeholder="Feed name" />
								</div>
								<div class="space-y-2">
									<Label for="feedUrl">Feed URL</Label>
									<Input id="feedUrl" v-model="newFeed.url" placeholder="https://example.com/feed.xml" />
								</div>
								<div class="space-y-2">
									<Label for="feedType">Type</Label>
									<Select v-model="newFeed.type">
										<SelectTrigger>
											<SelectValue placeholder="Select type" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="rss">RSS</SelectItem>
											<SelectItem value="atom">Atom</SelectItem>
											<SelectItem value="json">JSON Feed</SelectItem>
										</SelectContent>
									</Select>
								</div>
								<div class="flex items-center space-x-2">
									<Switch id="autoImport" v-model:checked="newFeed.autoImport" />
									<Label for="autoImport">Auto-import new items</Label>
								</div>
							</div>
							<DialogFooter>
								<Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
								<Button @click="handleCreate" :disabled="!newFeed.name || !newFeed.url">Add Feed</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
				</div>
			</CardHeader>
			<CardContent>
				<div class="space-y-4">
					<Input v-model="searchQuery" placeholder="Search feeds..." class="max-w-md" />

					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Type</TableHead>
								<TableHead>Status</TableHead>
								<TableHead class="text-center">Items</TableHead>
								<TableHead>Last Fetched</TableHead>
								<TableHead>Auto-Import</TableHead>
								<TableHead class="text-right">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							<template v-if="filteredFeeds.length > 0">
								<TableRow v-for="feed in filteredFeeds" :key="feed.id">
									<TableCell class="font-medium">
										<div>
											<div>{{ feed.name }}</div>
											<div class="text-xs text-muted-foreground truncate max-w-[200px]">{{ feed.url }}</div>
										</div>
									</TableCell>
									<TableCell>
										<Badge variant="outline">{{ feed.type.toUpperCase() }}</Badge>
									</TableCell>
									<TableCell>
										<Badge :variant="getStatusVariant(feed.status)">
											<component :is="getStatusIcon(feed.status)" class="h-3 w-3 mr-1" />
											{{ feed.status }}
										</Badge>
									</TableCell>
									<TableCell class="text-center">{{ feed.itemCount }}</TableCell>
									<TableCell class="text-sm text-muted-foreground">{{ feed.lastFetchDate || 'Never' }}</TableCell>
									<TableCell>
										<Badge :variant="feed.autoImport ? 'default' : 'outline'">
											{{ feed.autoImport ? 'On' : 'Off' }}
										</Badge>
									</TableCell>
									<TableCell class="text-right">
										<div class="flex justify-end space-x-1">
											<Button variant="ghost" size="sm" @click="handleRefresh(feed)">
												<RefreshCw class="h-4 w-4" />
											</Button>
											<DropdownMenu>
												<DropdownMenuTrigger as-child>
													<Button variant="ghost" size="sm">
														<EllipsisVerticalIcon class="h-4 w-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent>
													<DropdownMenuItem>Edit</DropdownMenuItem>
													<DropdownMenuItem>View Items</DropdownMenuItem>
													<DropdownMenuItem class="text-destructive" @click="handleDelete(feed)">Delete</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</div>
									</TableCell>
								</TableRow>
							</template>
							<TableRow v-else>
								<TableCell colspan="7" class="h-24 text-center text-muted-foreground">
									<div v-if="loading">Loading feeds...</div>
									<div v-else>No feeds configured. Add a feed to start importing content.</div>
								</TableCell>
							</TableRow>
						</TableBody>
					</Table>
				</div>
			</CardContent>
		</Card>
	</div>
</template>
